import { FC, useRef } from "react";
import {
  Button,
  ConfigProvider,
  Form,
  Input,
  message,
  Select,
  Upload,
  Tooltip,
} from "antd";
import TextArea from "antd/lib/input/TextArea";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import useModalCreateChat, { Props, ReceivedProps } from "./hook";
import {
  FileTag,
  EllipsisBadge,
  RemoveFile,
  FileList,
  TooltipFileList,
} from "./styled";

const CreateChatLayout: FC<Props> = ({
  handleBackToList,
  form,
  handleSubmit,
  ALLOWED_FILE_TYPES,
  ALLOWED_MIME_TYPES,
  categoryOptions,
  isLoading,
  fileList,
  setFileList,
  handleValuesChange,
  canSubmit,
}) => {
  const overLimitSizeRef = useRef(false);
  return (
    <ConfigProvider autoInsertSpaceInButton={false}>
      <div className="create-chat">
        {/* Header */}
        <div className="create-chat__header">
          <button
            type="button"
            className="create-chat__header__back"
            onClick={handleBackToList}
          >
            <ArrowBackIcon className="icon-back" />
            <span className="create-chat__header__back-label">戻る</span>
          </button>
          <p className="create-chat__header__title">新しい問い合わせ</p>
        </div>

        {/* Form */}
        <div className="create-chat__content">
          <Form
            form={form}
            onFinish={handleSubmit}
            layout="vertical"
            onValuesChange={handleValuesChange}
          >
            <div className="create-chat__form-items">
              <Form.Item label="件名（任意）" name="title">
                <Input
                  placeholder="未入力の場合はビズAIが内容から作成します"
                  onBlur={(e) => {
                    const trimmedValue = e.target.value.trim();
                    form.setFieldsValue({ title: trimmedValue });
                    handleValuesChange(null, form.getFieldsValue());
                  }}
                />
              </Form.Item>

              <Form.Item
                label="カテゴリー"
                name="category_id"
                className="form-item-custom"
                rules={[{ required: true, message: "必須項目です" }]}
              >
                <Select options={categoryOptions} className="select-custom" />
              </Form.Item>

              <Form.Item
                label="内容"
                name="content"
                rules={[
                  { required: true, message: "必須項目です", whitespace: true },
                ]}
              >
                <TextArea
                  placeholder="お困りの内容を詳しくお書きください..."
                  rows={4}
                  onBlur={(e) => {
                    const trimmedValue = e.target.value.trim();
                    form.setFieldsValue({ content: trimmedValue });
                    handleValuesChange(null, form.getFieldsValue());
                  }}
                />
              </Form.Item>

              <Form.Item
                label="ファイル添付"
                name="files"
                valuePropName="fileList"
                getValueFromEvent={(e) => e?.fileList}
              >
                <Upload
                  fileList={fileList}
                  accept={ALLOWED_FILE_TYPES.join(",")}
                  listType="picture"
                  // maxCount={5}
                  multiple
                  beforeUpload={(file) => {
                    const isImage = file.type.startsWith("image/");
                    const isVideo = file.type.startsWith("video/");
                    const isAllowed =
                      isImage ||
                      isVideo ||
                      ALLOWED_MIME_TYPES.includes(file.type);

                    if (!isAllowed) {
                      message.error(
                        "画像、動画、PDF、Word、Excel ファイルのみアップロードできます",
                      );
                      return Upload.LIST_IGNORE;
                    }

                    const isLt10M = file.size / 1024 / 1024 <= 10;
                    if (!isLt10M) {
                      message.error(
                        "総ファイルサイズは10MBまでの制限があります",
                      );
                      return Upload.LIST_IGNORE;
                    }

                    return false;
                  }}
                  onPreview={async (file) => {
                    let src = file.url as string;
                    if (!src && file.originFileObj) {
                      src = URL.createObjectURL(file.originFileObj);
                    }

                    if (file.type?.startsWith("image/")) {
                      const w = window.open(src);
                      w?.document.write(
                        `<img src="${src}" style="width:100%"/>`,
                      );
                      return;
                    }

                    window.open(src, "_blank");
                  }}
                  showUploadList={false}
                  onChange={({ fileList }) => {
                    let total = 0;
                    const validFiles = [];
                    for (const file of fileList) {
                      const size =
                        file.size ||
                        (file.originFileObj ? file.originFileObj.size : 0);
                      if (total + size > 10 * 1024 * 1024) {
                        break;
                      }
                      total += size;
                      validFiles.push(file);
                    }
                    if (
                      fileList.length > validFiles.length &&
                      !overLimitSizeRef.current
                    ) {
                      message.error(
                        "総ファイルサイズは10MBまでの制限があります",
                      );
                      overLimitSizeRef.current = true;
                      setTimeout(() => {
                        overLimitSizeRef.current = false;
                      }, 500);
                    }
                    form.setFieldsValue({ files: validFiles });
                    setFileList(validFiles);
                  }}
                  onRemove={(file) => {
                    setFileList((prev) =>
                      prev.filter((f) => f.uid !== file.uid),
                    );
                    return true;
                  }}
                >
                  <Button
                    className="btn-upload"
                    icon={<AttachFileIcon className="attach-icon" />}
                  >
                    ファイルを選択
                  </Button>
                  <p className="btn-upload-rcm">
                    総ファイルサイズは10MBまでの制限があります
                  </p>
                </Upload>
              </Form.Item>
            </div>

            <FileList>
              {fileList.slice(0, 3).map((file) => (
                <FileTag key={file.uid}>
                  <span className="file-name">{file.name}</span>
                  <RemoveFile
                    onClick={() => {
                      setFileList((prev) =>
                        prev.filter((f) => f.uid !== file.uid),
                      );
                      form.setFieldsValue({
                        files: fileList.filter((f) => f.uid !== file.uid),
                      });
                    }}
                  >
                    ×
                  </RemoveFile>
                </FileTag>
              ))}
              {fileList.length > 3 && (
                <Tooltip
                  color="#ffffff"
                  overlayInnerStyle={{
                    color: "#000000",
                    padding: "8px",
                    boxShadow:
                      "0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)",
                  }}
                  title={
                    <TooltipFileList>
                      {fileList.slice(3).map((file) => (
                        <FileTag key={file.uid}>
                          <span className="file-name">{file.name}</span>
                          <RemoveFile
                            onClick={(e) => {
                              e.stopPropagation();
                              setFileList((prev) =>
                                prev.filter((f) => f.uid !== file.uid),
                              );
                              form.setFieldsValue({
                                files: fileList.filter(
                                  (f) => f.uid !== file.uid,
                                ),
                              });
                            }}
                          >
                            ×
                          </RemoveFile>
                        </FileTag>
                      ))}
                    </TooltipFileList>
                  }
                >
                  <EllipsisBadge>...</EllipsisBadge>
                </Tooltip>
              )}
            </FileList>

            <div className="footer-chat">
              <Button
                className="btn-submit"
                type="primary"
                htmlType="submit"
                loading={isLoading}
                disabled={!canSubmit}
              >
                送信
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </ConfigProvider>
  );
};
const CreateChat: FC<ReceivedProps> = (props) => (
  <CreateChatLayout {...useModalCreateChat(props)} />
);
export default CreateChat;
